import { Collapse, Button, FormInstance, Form, Spin } from 'antd';
import dayjs from 'dayjs';
import { Fragment, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { MeetingType } from 'commons/types/meetingsTypes';
import { useTimeslotList } from 'stores/timeslots/timeslotsHooks';
import { Patient } from 'commons/types/patientsTypes';
import { serviceFromZip } from 'commons/libs/meeting_utils';
import { REQUEST_STATUSES } from 'commons/types/common';
import { useServices } from 'stores/user/userHooks';

interface Date {
  key: number;
  label: string;
  children: ReactNode;
}
interface ScheduleDateStepProps {
  patient: Patient | undefined;
  onNext: () => void;
  form: FormInstance;
  nextRdvType: MeetingType;
  doctorID?: string;
  scroll?: boolean;
  setServiceID?: (serviceID: string) => void;
}

export default function ScheduleDateStep({
  onNext,
  patient,
  form,
  nextRdvType,
  doctorID = '',
  scroll = false,
  setServiceID = () => {},
}: ScheduleDateStepProps) {
  const [services] = useServices();
  const serviceID = useMemo(
    () => serviceFromZip(services, patient?.zip || ''),
    [patient?.zip, services],
  );
  const [timeslots, fetchingTimeslotRequestStatus] = useTimeslotList(
    new URLSearchParams({
      rdv_type: nextRdvType,
      serviceID,
      doctorID,
    }),
  );
  const onClick = useCallback(
    (date: string, docID: string) => () => {
      form.setFieldValue('meeting', {
        doctorID: docID,
        date,
      });
      onNext();
    },
    [form, onNext],
  );

  const items: Date[] = useMemo(() => {
    return timeslots
      ? Object.entries(timeslots).map(([date, hours], index) => ({
          key: index,
          label: dayjs(date).format('dddd D MMMM YYYY'),
          children: (
            <div className='flex flex-row flex-wrap'>
              {Object.entries(hours).map(([hour, doctors]) => {
                const indice = Math.floor(Math.random() * doctors.length);
                return (
                  <Form.Item
                    name='meeting'
                    className='!mx-2 !mb-2'
                    key={`${date}-${hour}`}
                  >
                    <Button
                      className='!border-2 !border-secondary w-[88px] !h-10 !font-bold !p-0'
                      onClick={onClick(`${date} ${hour}`, doctors[indice][0])}
                    >
                      {hour}
                    </Button>
                  </Form.Item>
                );
              })}
            </div>
          ),
        }))
      : [];
  }, [onClick, timeslots]);

  useEffect(() => {
    setServiceID(serviceID);
    form.setFieldValue('serviceID', serviceID);
  }, [form, serviceID, setServiceID]);

  if (fetchingTimeslotRequestStatus !== REQUEST_STATUSES.SUCCESS) {
    return (
      <div className='w-full text-center'>
        <Spin spinning />
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      {/* {nextRdvType === MEETING_TYPES.RDV_2 && (
        <div className='w-full pb-4 text-center'>
          <Select
            value={service}
            onChange={setService}
            options={getServices()}
          />
        </div>
      )} */}
      {items.length === 0 ? (
        <span className='font-normal normal-case '>
          Il n&apos;y a plus de créneaux disponibles. Merci de réessayer plus
          tard…
        </span>
      ) : (
        <Fragment>
          <p className='w-full text-xs text-left'>Choisissez votre date</p>
          <div className={scroll ? 'overflow-y-auto max-h-96' : ''}>
            <Collapse
              accordion
              defaultActiveKey={['0']}
              bordered={false}
              className='w-full text-secondary font-bold px-6 pb-6 !text-base capitalize'
            >
              {items.map(({ key, label, children }) => (
                <Collapse.Panel
                  className='!bg-primary-light rounded-2xl mb-2 !border-none'
                  header={<p>{label}</p>}
                  key={key}
                >
                  {children}
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>
        </Fragment>
      )}
    </div>
  );
}
